import { Autocomplete, TextField } from '@mui/material'
import { FC, useCallback, useEffect, useState } from 'react'
import { useDebounce } from 'use-debounce'
import { Maybe, Vendor, useFetchAllVendorsLazyQuery } from '../../../../generated'
import { PAGE_LIMIT } from '../../../constants'

interface VendorFilterProps {
  vendorId: string | number | undefined
  setReportFilters: React.Dispatch<React.SetStateAction<Record<string, any>>>
}

const VendorFilter: FC<VendorFilterProps> = ({ vendorId, setReportFilters }) => {
  const [vendors, setVendors] = useState<Maybe<Vendor>[]>([])
  const [selectedVendor, setSelectedVendor] = useState<Vendor | null>(null)
  const [vendorPage, setVendorPage] = useState(1)
  const [hasMorePages, setHasMorePages] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearch] = useDebounce(searchTerm, 500)

  const [fetchVendors, { loading }] = useFetchAllVendorsLazyQuery({
    fetchPolicy: 'cache-first',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
    onCompleted(data) {
      const result = data?.fetchAllVendors
      if (!result) return
      const { vendors: rawVendors, pagination } = result
      const newVendors = (rawVendors || []) as Maybe<Vendor>[]
      const { page: currentPage, totalPages } = pagination || {}
      if (rawVendors) {
        setVendors(prev => (vendorPage === 1 ? newVendors : [...prev, ...newVendors]))
        setHasMorePages(currentPage && totalPages ? currentPage < totalPages : false)
      }
    },
    onError() {
      setVendors([])
      setHasMorePages(false)
    }
  })

  useEffect(() => {
    setVendors([])
    setVendorPage(1)
    setHasMorePages(true)
  }, [debouncedSearch])

  useEffect(() => {
    if (!hasMorePages) return
    fetchVendors({
      variables: {
        fetchVendorsInput: {
          keyword: debouncedSearch.trim(),
          paginationOptions: { limit: PAGE_LIMIT, page: vendorPage }
        }
      }
    })
  }, [vendorPage, debouncedSearch, fetchVendors, hasMorePages])

  // Keep the selected vendor available in the options list even when it is not in the current search page
  useEffect(() => {
    if (!vendorId) {
      setSelectedVendor(null)
      return
    }
    const match = (vendors.filter(Boolean) as Vendor[]).find(v => String(v.id) === String(vendorId))
    if (match) setSelectedVendor(match)
  }, [vendorId, vendors])

  const getOptionLabel = (option: Vendor) =>
    option?.vendorId ? `(#${option.vendorId}) ${option.name || ''}` : option?.name || ''

  const options = [
    ...(selectedVendor ? [selectedVendor] : []),
    ...(vendors.filter(Boolean) as Vendor[]).filter(v => v.id !== selectedVendor?.id)
  ]

  const handleScroll = useCallback(
    (event: React.SyntheticEvent) => {
      const node = event.currentTarget
      if (node.scrollTop + node.clientHeight >= node.scrollHeight - 5 && hasMorePages && !loading) {
        setVendorPage(prev => prev + 1)
      }
    },
    [hasMorePages, loading]
  )

  return (
    <Autocomplete
      options={options}
      value={selectedVendor}
      getOptionLabel={option => getOptionLabel(option as Vendor)}
      isOptionEqualToValue={(option, value) => option?.id === value?.id}
      onChange={(_, newValue) => {
        setSelectedVendor(newValue)
        setReportFilters(prev => ({ ...prev, vendorId: newValue?.id }))
      }}
      onInputChange={(_, newInput, reason) => {
        if (reason === 'input') setSearchTerm(newInput)
        else if (reason === 'clear') setSearchTerm('')
      }}
      renderInput={params => <TextField {...params} label="Vendor" />}
      ListboxProps={{
        role: 'list-box',
        style: { maxHeight: '190px' },
        onScroll: handleScroll
      }}
      loading={loading}
      noOptionsText={loading ? 'Loading...' : 'No vendors'}
      sx={{ minWidth: 250, maxWidth: 400 }}
    />
  )
}

export default VendorFilter
